import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Lightweight Google Analytics 4 hook.
 *
 * Behaviour:
 *  • Reads the Measurement ID from process.env.REACT_APP_GA4_ID.
 *  • If the ID is missing OR still the placeholder ("G-XXXXXXXXXX"), does absolutely nothing —
 *    no script tag is injected and no requests fire. Swap in the real ID to switch tracking on.
 *  • Loads gtag.js exactly once, with `send_page_view: false` so we can drive page_view events
 *    ourselves on client-side route changes.
 *  • Skips all /admin routes (login + dashboard) so internal usage is never counted.
 */
const PLACEHOLDER_ID = "G-XXXXXXXXXX";

function isTrackable(pathname) {
  return !pathname.startsWith("/admin");
}

export default function useGa4() {
  const location = useLocation();
  const loadedRef = useRef(false);

  useEffect(() => {
    const id = process.env.REACT_APP_GA4_ID;
    if (!id || id === PLACEHOLDER_ID) return;
    if (!isTrackable(location.pathname)) return;

    // Load gtag.js once
    if (!loadedRef.current) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
      document.head.appendChild(s);

      window.dataLayer = window.dataLayer || [];
      function gtag() { window.dataLayer.push(arguments); }
      window.gtag = gtag;
      gtag("js", new Date());
      gtag("config", id, { send_page_view: false, anonymize_ip: true });
      loadedRef.current = true;
    }

    // Fire page_view for the current route
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_title: document.title,
        page_location: window.location.href,
      });
    }
  }, [location.pathname, location.search]);
}
